// Gesture classification mirroring control.html, ported to React.
//
// Key design notes (from iterating with Codex/Cursor/Claude Desktop):
// - Tap fires on `click` event, NOT on pointerup — this is what lets
//   embedded host pickers (Codex "add to context") suppress device taps
//   just by preventDefault'ing the click event.
// - Drag fires from pointer events because hosts don't intercept those.
// - pressDuration = hold time before first movement (maps to /drag API).
// - holdDuration  = dwell at end before release (iOS drop zones).

export const TAP_DIST = 8;       // px (device coords) considered "no movement"
export const TAP_MS = 300;       // beyond this, a no-move press isn't a tap
export const MIN_DRAG = 30;      // ms floor for drag duration
export const HOLD_IDLE_MS = 60;  // end-idle ≥ this → count as holdDuration
// Bridges (iOS WDA, Android input swipe) replay the drag synchronously for
// the full `duration`, so passing the user's real move time makes fast flicks
// feel fine but turns slow/deliberate drags into a visible lag while the
// device re-performs them. Cap the move portion so playback always feels
// snappy; pressDuration + holdDuration are preserved for long-press and
// drop-zone semantics.
export const MAX_DRAG_MS = 300;

export interface DevicePoint {
  x: number;
  y: number;
}

export interface DragPayload {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  duration: number;      // ms
  pressDuration: number; // ms
  holdDuration: number;  // ms
}

export interface TapPayload {
  x: number;
  y: number;
}

export type GestureResult =
  | { kind: "tap"; payload: TapPayload }
  | { kind: "drag"; payload: DragPayload };

interface Pending {
  id: number;
  x: number;
  y: number;
  downT: number;
  moveStartT: number; // 0 until first move past TAP_DIST
  lastMoveT: number;
  isDrag: boolean;
}

/**
 * Stateful gesture recognizer. Feed it pointer events; it emits drag via
 * `onDrag`. Taps are NOT emitted by this — the caller must attach its own
 * `click` handler so hosts that blockedDefault the click also block the tap.
 */
export class GestureRecognizer {
  private pending: Pending | null = null;
  private pendingTapPoint: { x: number; y: number; t: number } | null = null;

  constructor(
    private readonly toDevice: (e: { clientX: number; clientY: number }) => DevicePoint,
    private readonly onDrag: (p: DragPayload) => void,
    private readonly onTap: (p: TapPayload) => void,
  ) {}

  onPointerDown(e: PointerEvent): void {
    const p = this.toDevice(e);
    this.pending = {
      id: e.pointerId,
      x: p.x,
      y: p.y,
      downT: performance.now(),
      moveStartT: 0,
      lastMoveT: 0,
      isDrag: false,
    };
  }

  onPointerMove(e: PointerEvent, capture: (pointerId: number) => void): void {
    const p = this.pending;
    if (!p || p.id !== e.pointerId) return;
    const now = performance.now();
    const pt = this.toDevice(e);
    if (p.moveStartT === 0) {
      if (Math.hypot(pt.x - p.x, pt.y - p.y) > TAP_DIST) {
        p.moveStartT = now;
        p.lastMoveT = now;
        p.isDrag = true;
        capture(e.pointerId);
      }
    } else {
      p.lastMoveT = now;
    }
  }

  onPointerUp(e: PointerEvent): void {
    const p = this.pending;
    if (!p || p.id !== e.pointerId) {
      this.pending = null;
      return;
    }
    this.pending = null;

    if (!p.isDrag) {
      // Defer to click event — preserves host-picker suppression.
      this.pendingTapPoint = { x: p.x, y: p.y, t: performance.now() };
      return;
    }

    const end = this.toDevice(e);
    const upT = performance.now();
    const pressDuration = Math.max(0, Math.round(p.moveStartT - p.downT));
    const rawDuration = Math.max(MIN_DRAG, Math.round(p.lastMoveT - p.moveStartT));
    const duration = Math.min(MAX_DRAG_MS, rawDuration);
    const idleAtEnd = Math.max(0, Math.round(upT - p.lastMoveT));
    const holdDuration = idleAtEnd >= HOLD_IDLE_MS ? idleAtEnd : 0;

    this.onDrag({
      fromX: p.x,
      fromY: p.y,
      toX: end.x,
      toY: end.y,
      duration,
      pressDuration,
      holdDuration,
    });
  }

  onPointerCancel(): void {
    this.pending = null;
    this.pendingTapPoint = null;
  }

  /** Called from the element's `click` event — tap fires only if this runs. */
  onClick(): void {
    const t = this.pendingTapPoint;
    this.pendingTapPoint = null;
    if (!t) return;
    if (performance.now() - t.t > 500) return;
    this.onTap({ x: t.x, y: t.y });
  }
}
